import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, 
  ArrowRight,
  Shield,
  Home,
  Target,
  Users,
  Save,
  User,
  Check,
  X,
  Ban,
  ShieldOff,
  Edit3
} from "lucide-react";
import { useLanguage } from "@/i18n";

export default function PermissionsPage({ evaluator }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [evaluators, setEvaluators] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedEvaluator, setSelectedEvaluator] = useState(null);
  const [blockedPlayerIds, setBlockedPlayerIds] = useState([]);
  const [canEditPlayers, setCanEditPlayers] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedEvaluator) {
      fetchPermissions(selectedEvaluator.id);
    }
  }, [selectedEvaluator]);

  const fetchData = async () => {
    try {
      const [evaluatorsRes, playersRes] = await Promise.all([
        axios.get(`${API}/evaluators`),
        axios.get(`${API}/players`)
      ]);
      // Filter out admins - they don't need permissions
      const nonAdminEvaluators = evaluatorsRes.data.filter(e => e.role !== 'admin');
      setEvaluators(nonAdminEvaluators);
      setPlayers(playersRes.data);
      if (nonAdminEvaluators.length > 0) {
        setSelectedEvaluator(nonAdminEvaluators[0]);
      }
    } catch (error) {
      toast.error(t('toasts.errorLoadingData'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPermissions = async (evaluatorId) => {
    try {
      const res = await axios.get(`${API}/evaluator-permissions/${evaluatorId}`);
      setBlockedPlayerIds(res.data.blocked_player_ids || []);
      setCanEditPlayers(res.data.can_edit_players !== false); // Default to true
    } catch (error) {
      setBlockedPlayerIds([]);
      setCanEditPlayers(true);
    }
  };

  const getPlayerName = (player) => {
    if (player.first_name) return `${player.first_name} ${player.last_name}`;
    return player.name || '';
  };

  const togglePlayer = (playerId) => {
    if (blockedPlayerIds.includes(playerId)) {
      setBlockedPlayerIds(blockedPlayerIds.filter(id => id !== playerId));
    } else {
      setBlockedPlayerIds([...blockedPlayerIds, playerId]);
    }
  };

  const blockAll = () => {
    setBlockedPlayerIds(players.map(p => p.id));
  };

  const blockNone = () => {
    setBlockedPlayerIds([]);
  };

  const handleSave = async () => {
    if (!selectedEvaluator) return;
    
    setIsSaving(true);
    try {
      await axios.put(`${API}/evaluator-permissions/${selectedEvaluator.id}`, {
        blocked_player_ids: blockedPlayerIds,
        can_edit_players: canEditPlayers
      });
      toast.success(t('toasts.permissionsSaved'));
    } catch (error) {
      toast.error(t('toasts.errorSavingPermissions'));
    } finally {
      setIsSaving(false);
    }
  };

  // Group players by position
  const playersByPosition = {};
  players.forEach(player => {
    const pos = player.position || 'Outros';
    if (!playersByPosition[pos]) playersByPosition[pos] = [];
    playersByPosition[pos].push(player);
  });

  const canAccess = evaluator?.role === 'admin';
  const allowedCount = players.length - blockedPlayerIds.length;

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Acesso Restrito</h2>
            <p className="text-muted-foreground">Apenas administradores podem gerenciar permissões.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Voltar ao Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">Permissões de Avaliação</h1>
                <p className="text-sm text-muted-foreground">Selecione os jogadores que cada avaliador <strong className="text-red-400">NÃO</strong> pode avaliar</p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={isSaving || !selectedEvaluator}>
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? "Salvando..." : "Salvar Permissões"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Carregando...</div>
        ) : evaluators.length === 0 ? (
          <Card className="bg-card border-border/50">
            <CardContent className="pt-6 text-center">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhum avaliador (não-admin) cadastrado.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Evaluator Selection */}
            <Card className="bg-card border-border/50 shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Selecionar Avaliador
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedEvaluator?.id || ""}
                  onValueChange={(v) => setSelectedEvaluator(evaluators.find(e => e.id === v))}
                >
                  <SelectTrigger className="bg-input/50">
                    <SelectValue placeholder="Selecione um avaliador" />
                  </SelectTrigger>
                  <SelectContent>
                    {evaluators.map(ev => (
                      <SelectItem key={ev.id} value={ev.id}>
                        <div className="flex items-center gap-2">
                          {ev.name}
                          <Badge variant="secondary" className="text-xs capitalize">{ev.role || 'avaliador'}</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedEvaluator && (
                  <div className="mt-4 p-4 rounded-lg bg-secondary/30">
                    <p className="font-medium">{selectedEvaluator.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedEvaluator.email}</p>
                    <Badge variant="outline" className="mt-2 capitalize">{selectedEvaluator.role || 'avaliador'}</Badge>
                    
                    {/* Edit Players Permission Toggle */}
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <div className={`flex items-center justify-between p-3 rounded-lg border ${canEditPlayers ? 'bg-blue-500/10 border-blue-500/30' : 'bg-slate-500/10 border-slate-500/30'}`}>
                        <div className="flex items-center gap-2">
                          <Edit3 className={`h-4 w-4 ${canEditPlayers ? 'text-blue-400' : 'text-slate-400'}`} />
                          <span className={`text-sm font-medium ${canEditPlayers ? 'text-blue-400' : 'text-slate-400'}`}>
                            Pode editar jogadores
                          </span>
                        </div>
                        <Switch
                          checked={canEditPlayers}
                          onCheckedChange={setCanEditPlayers}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {canEditPlayers 
                          ? "Este avaliador pode editar dados dos jogadores" 
                          : "Este avaliador NÃO pode editar jogadores"}
                      </p>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-border/50 grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                        <p className="text-2xl font-bold text-green-400">{allowedCount}</p>
                        <p className="text-xs text-green-400/80">✓ PODE AVALIAR</p>
                      </div>
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center">
                        <p className="text-2xl font-bold text-red-400">{blockedPlayerIds.length}</p>
                        <p className="text-xs text-red-400/80">✗ BLOQUEADOS</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={blockNone} className="flex-1 border-green-500/50 text-green-500 hover:bg-green-500/10">
                    <Check className="mr-1 h-4 w-4" />
                    Permitir Todos
                  </Button>
                  <Button variant="outline" size="sm" onClick={blockAll} className="flex-1 border-red-500/50 text-red-500 hover:bg-red-500/10">
                    <Ban className="mr-1 h-4 w-4" />
                    Bloquear Todos
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Player Selection - Two columns: Allowed and Blocked */}
            <div className="lg:col-span-2">
              <Card className="bg-card border-border/50 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Gerenciar Permissões
                  </CardTitle>
                  <CardDescription>
                    Clique nos jogadores para alternar entre <span className="text-green-400 font-medium">PERMITIDO</span> e <span className="text-red-400 font-medium">BLOQUEADO</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Allowed Players Column */}
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-widest text-green-400 mb-3 flex items-center gap-2 pb-2 border-b border-green-500/30">
                        <Check className="h-4 w-4" />
                        PODE AVALIAR ({allowedCount})
                      </h3>
                      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                        {players.filter(p => !blockedPlayerIds.includes(p.id)).map(player => (
                          <div
                            key={player.id}
                            className="flex items-center gap-3 p-3 rounded-lg border border-green-500/30 bg-green-500/5 cursor-pointer transition-all hover:border-red-500/30 hover:bg-red-500/5"
                            onClick={() => togglePlayer(player.id)}
                          >
                            <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                              <Check className="h-4 w-4 text-green-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{player.first_name} {player.last_name}</p>
                              <p className="text-xs text-muted-foreground">{player.position}</p>
                            </div>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        ))}
                        {players.filter(p => !blockedPlayerIds.includes(p.id)).length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">Nenhum jogador permitido</p>
                        )}
                      </div>
                    </div>

                    {/* Blocked Players Column */}
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-widest text-red-400 mb-3 flex items-center gap-2 pb-2 border-b border-red-500/30">
                        <Ban className="h-4 w-4" />
                        NÃO PODE AVALIAR ({blockedPlayerIds.length})
                      </h3>
                      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                        {players.filter(p => blockedPlayerIds.includes(p.id)).map(player => (
                          <div
                            key={player.id}
                            className="flex items-center gap-3 p-3 rounded-lg border border-red-500/30 bg-red-500/5 cursor-pointer transition-all hover:border-green-500/30 hover:bg-green-500/5"
                            onClick={() => togglePlayer(player.id)}
                          >
                            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{player.first_name} {player.last_name}</p>
                              <p className="text-xs text-muted-foreground">{player.position}</p>
                            </div>
                            <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                              <Ban className="h-4 w-4 text-red-500" />
                            </div>
                          </div>
                        ))}
                        {blockedPlayerIds.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">Nenhum jogador bloqueado</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
